import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Form, Input, Button, Typography, Alert } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import '../common.css';

const { Title, Text } = Typography;

function SignIn({ setUserData }) {
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuth(); 
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleSignIn = async (values) => {
    setEmailError('');
    setPasswordError('');
    setLoading(true);

    try {
      const { email, password } = values;
      const deviceName = navigator.userAgent;

      const response = await fetch(`${API_URL}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password, 
          deviceName 
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const { accessToken, refreshToken, userId, role, isTempPassword } = responseData;

        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('userId', userId);
        localStorage.setItem('role', role);
        signIn(accessToken);
        setUserData({ userId, role });

        if (isTempPassword) {
          navigate('/change-password');
        } else {
          navigate('/dashboard');
        }
      } else {
        const errorResponse = await response.json();
        switch (errorResponse.error) {
          case 'UserNotFound':
            setEmailError('The email address is not registered.');
            break;
          case 'InvalidPassword':
            setPasswordError('The password is incorrect.');
            break;
          default:
            setEmailError('Email or password is incorrect.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setEmailError('Email or password is incorrect.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
    <div 
      style={{
        background: 'white',
        padding: '2.5rem',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Decorative element */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '5px',
      }}></div>
  
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <Title 
          level={2} 
          style={{ 
            margin: 0,
            fontSize: '26px',
            fontWeight: '600',
            color: '#1a1a1a'
          }}
        >
          Welcome!
        </Title>
        <Text type="secondary" style={{ marginTop: '0.5rem', display: 'block' }}>
          Sign in to access your account
        </Text>
      </div>
  
      <Form
        layout="vertical"
        onFinish={handleSignIn}
        requiredMark={false}
        size="large"
      >
        <Form.Item
          name="email"
          label="Email"
          style={{ marginBottom: '1.5rem' }}
          rules={[
            { required: true, message: 'Please input your email!' },
            { type: 'email', message: 'Please enter a valid email!' },
          ]}
        >
          <Input 
            placeholder="Enter your email" 
            style={{
              height: '45px',
              borderRadius: '6px'
            }}
            prefix={<MailOutlined style={{ color: '#bfbfbf', marginRight: '8px' }} />}
          />
        </Form.Item>
  
        <Form.Item
          name="password"
          label="Password"
          style={{ marginBottom: '1.5rem' }}
          rules={[
            { required: true, message: 'Please input your password!' }
          ]}
        >
          <Input.Password 
            placeholder="Enter your password" 
            style={{
              height: '45px',
              borderRadius: '6px'
            }}
            prefix={<LockOutlined style={{ color: '#bfbfbf', marginRight: '8px' }} />}
          />
        </Form.Item>
  
        {emailError && (
          <Alert
            message={emailError}
            type="error"
            showIcon
            style={{ marginBottom: '1rem', borderRadius: '6px' }}
          />
        )}
        {passwordError && (
          <Alert
            message={passwordError}
            type="error"
            showIcon
            style={{ marginBottom: '1rem', borderRadius: '6px' }}
          />
        )}
  
        <Form.Item style={{ marginBottom: '1rem' }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            block
            loading={loading}
            style={{
              height: '45px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: '#1677ff',
              color: '#fff',
              borderColor: '#52c41a',
              boxShadow: '0 2px 8px rgba(22, 119, 255, 0.15)'
            }}
          >
            Sign In
          </Button>
        </Form.Item>
        
        <div style={{ textAlign: 'center', marginTop: '1.2rem' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Please contact school administrators if you have forgotten your login email or password
          </Text>
        </div>
      </Form>
    </div>
  </div>
  );
}

SignIn.propTypes = {
  setUserData: PropTypes.func.isRequired,
};

export default SignIn;